import RawCard from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import RawCardHeader from '@mui/material/CardHeader';
import RawPermIdentityIcon from '@mui/icons-material/PermIdentity';
import RawMailOutlineIcon from '@mui/icons-material/MailOutline';
import RawFileOpenIcon from '@mui/icons-material/FileOpen';
import RawPhoneIcon from '@mui/icons-material/Phone';
import RawWorkOutlineIcon from '@mui/icons-material/WorkOutline';
import RawHomeWorkIcon from '@mui/icons-material/HomeWork';
import RawHttpsIcon from '@mui/icons-material/Https';
import RawTodayIcon from '@mui/icons-material/Today';
import RawLocalOfferIcon from '@mui/icons-material/LocalOffer';
import RawAttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RawEmojiSymbolsIcon from '@mui/icons-material/EmojiSymbols';
import RawAccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RawVisibilityIcon from '@mui/icons-material/Visibility';
import RawIconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '../atoms/Menu';
import { Theme, styled } from '@mui/material/styles';
import {
  ISociety,
  BrowserBloomer,
  IContact,
  IMissionCardDetails,
  ExtensionAssessmentGQL,
  ATSOrigin,
} from '@bloomays-lib/types.shared';
import { convertDateToStringFormat, hasIbanSupport } from '@bloomays-lib/utils.shared';
import { useTranslation } from 'react-i18next';
import { getAssessmentKey } from '../../helpers/locales';
import { useEffect, useRef, useState } from 'react';
import TagMissionType from './TagMissionType';
import { Button, Carousel, FileViewer, Modal } from '@bloomays-lib/ui.shared';
import { BloomerCard } from './BloomerCard';
import { useAuthStore } from '../../stores';
import EditBloomerSociety, { EditBloomerSocietyRef } from '../organisms/EditBloomerSociety';
import { GET_ONE_FULL_MISSION, UPSERT_BLOOMER_SOCIETY } from '@bloomays-lib/adapter.api-bloomer';
import { useMutation } from '@apollo/client';
import { cancel, notify } from '../../helpers/toastify';
import { errorLogger } from '../../helpers/error';

export type SocietyCard = {
  type: 'society';
  data: {
    society?: ISociety;
    mission: IMissionCardDetails;
    extraInfoTitle?: string;
    path?: string;
  };
};

export type BloomerCardType = {
  type: 'bloomer';
  data: {
    bloomer?: BrowserBloomer;
    missionOrigin?: ATSOrigin;
  };
};

export type ContactCard = {
  type: 'contact';
  data: {
    contact?: IContact | IContact[];
    extraInfoTitle?: string;
  };
};

type Action = {
  name: string;
  action: () => void;
};

export type MissionCard = {
  type: 'mission';
  data: {
    mission: IMissionCardDetails;
    actions: Action[];
    currentExtensionAssessment: ExtensionAssessmentGQL | null;
  };
};

export type MissionDetailCardProps = SocietyCard | BloomerCardType | ContactCard | MissionCard;

const CurrentExtensionAssessmentLine = (props: { extensionAssessment: ExtensionAssessmentGQL | null }) => {
  const { t } = useTranslation(['extendShortenMission']);
  const { extensionAssessment } = props;

  let assessmentLabel = t('nonExistingAssessment');
  if (extensionAssessment) {
    const { assessment, newEndDate } = extensionAssessment;
    const assessmentText = t(getAssessmentKey(assessment));
    assessmentLabel =
      assessment > 0.5 ? t('assessmentLabel', { assessmentLabel: assessmentText, newEndDate }) : t(assessmentText);
  }

  return (
    <ContainerLine>
      <CenteredLabel id="taxesMissionCard" variant="body1Light" component="p">
        <EmojiSymbolsIcon />
        {t('assessmentLabelInDetailCard')}
      </CenteredLabel>
      <ValueTypography id="taxesMissionCardValue" variant="body1Light">
        {assessmentLabel}
      </ValueTypography>
    </ContainerLine>
  ) as React.ReactNode;
};

const getContactComponent = (contact?: IContact) => {
  return (
    <ContainerData>
      <ContainerLine>
        <CenteredLabel variant="body1Light" component="p">
          <PermIdentityIcon />
          Nom complet:
        </CenteredLabel>
        <DarkTypo variant="body1Light">
          {contact?.fullname || (contact?.firstname && `${contact?.firstname} ${contact?.lastname}`)}
        </DarkTypo>
      </ContainerLine>
      <ContainerLine>
        <CenteredLabel variant="body1Light" component="p">
          <MailOutlineIcon />
          Email:
        </CenteredLabel>
        <DarkTypo variant="body1Light">
          <HrefLine href={`mailto:${contact?.email}`}>{contact?.email}</HrefLine>
        </DarkTypo>
      </ContainerLine>
      <ContainerLine>
        <CenteredLabel variant="body1Light" component="p">
          <PhoneIcon />
          Téléphone:
        </CenteredLabel>
        <DarkTypo variant="body1Light">
          <HrefLine href={`tel:${contact?.phoneNumber}`}>{contact?.phoneNumber}</HrefLine>
        </DarkTypo>
      </ContainerLine>
      <ContainerLine>
        <CenteredLabel variant="body1Light" component="p">
          <WorkOutlineIcon />
          Job:
        </CenteredLabel>
        <DarkTypo variant="body1Light">{contact?.nameJob}</DarkTypo>
      </ContainerLine>
    </ContainerData>
  );
};

const MissionDetailCard = ({ type, data }: MissionDetailCardProps) => {
  const [token, setToken] = useState<string | undefined>();
  const auth = useAuthStore((state) => state.auth);
  const [openPopinEditSociety, setOpenPopinEditSociety] = useState<boolean>(false);
  const formSociety = useRef<EditBloomerSocietyRef>(null);
  const { t } = useTranslation(['notify', 'mission']);
  const [upsertSociety] = useMutation<{ upsertBloomerSociety: ISociety }>(UPSERT_BLOOMER_SOCIETY);
  useEffect(() => {
    if (auth?.getAccessTokenSilently) {
      auth.getAccessTokenSilently().then((token) => {
        setToken(token);
      });
    }
  }, [auth]);
  const [filePath, setFilePath] = useState<string | undefined>(undefined);

  const submitForm = (e: React.FormEvent<HTMLFormElement>): void => {
    formSociety?.current?.validate(e);
  };

  const upsertBloomerSocietyData = async (society: ISociety, mission: IMissionCardDetails) => {
    try {
      notify('info', `${t('updateInProgress')}`, null, {
        hideProgressBar: false,
        autoClose: 10000,
      });
      const refetchQueries = [
        {
          query: GET_ONE_FULL_MISSION,
          variables: {
            recordId: mission.recordId,
          },
        },
      ];
      const upload = await upsertSociety({
        variables: {
          input: {
            name: society.name,
            recordId: society.recordId,
            status: society.status,
            siret: society.siret,
            KBIS: society.KBIS,
            KbisExpirationDate: society.KbisExpirationDate,
            URSSAFVigilanceCertificate: society.URSSAFVigilanceCertificate,
            URSSAFVCExpirationDate: society.URSSAFVCExpirationDate,
            RCPVigilanceCertificate: society.RCPVigilanceCertificate,
            RCPExpirationDate: society.RCPExpirationDate,
            IBAN: society.IBAN,
            BIC: society.BIC,
            portage: society.portage,
            tva: society.tva,
            addressCountryCode: society.addressCountryCode,
            bloomerBillingMission: [mission.recordId],
          },
        },
        refetchQueries,
      });
      cancel();
      notify('success', `${t('updateSuccess')}`, null, {
        hideProgressBar: true,
        autoClose: 2000,
      });
      return upload?.data?.upsertBloomerSociety;
    } catch (error: any) {
      cancel();
      notify('error', `${t('randomUpdateError')}`, error);
      errorLogger(error, {
        extraInfos: 'extraErrorInfos',
      });
    }
  };

  switch (type) {
    case 'society': {
      const { extraInfoTitle, society, mission } = data;
      const supportIban = hasIbanSupport(society?.status, society?.addressCountryCode ?? undefined);
      return (
        <Card>
          <Modal
            open={!!filePath}
            onClose={() => setFilePath(undefined)}
            additionnalCSS={{
              width: '822px',
              padding: '32px',
              border: 'none',
              borderRadius: '20px',
              height: '98%',
            }}
          >
            <FileViewer width="100%" height="100%" url={filePath as string} accessToken={token} />
          </Modal>
          <Modal
            open={openPopinEditSociety}
            onClose={() => {
              setOpenPopinEditSociety(false);
            }}
            additionnalCSS={{
              width: '822px',
              padding: '32px',
              border: 'none',
              borderRadius: '20px',
              height: '90%',
              overflow: 'auto',
            }}
          >
            <form onSubmit={submitForm}>
              <EditBloomerSociety
                ref={formSociety}
                onValidated={async (updatedSociety: ISociety) => {
                  const savedSociety = await upsertBloomerSocietyData(updatedSociety, mission);
                  if (savedSociety) {
                    setOpenPopinEditSociety(false);
                  }
                  return savedSociety;
                }}
                society={society}
                hideSubmitButton={false}
              />
            </form>
          </Modal>
          <CardContent>
            <CardHeader
              title={`La societé ${extraInfoTitle ? extraInfoTitle : ''}`}
              titleTypographyProps={{ variant: 'heading6' }}
            />
            <ContainerData>
              <ContainerLine>
                <CenteredLabel variant="body1Light" component="p">
                  <PermIdentityIcon />
                  Nom:
                </CenteredLabel>
                <ValueTypography variant="body1Light">{society?.name}</ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel variant="body1Light" component="p">
                  <HomeWorkIcon />
                  Statut:
                </CenteredLabel>
                <ValueTypography variant="body1Light">{society?.status}</ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel variant="body1Light" component="p">
                  <HttpsIcon />
                  Siret:
                </CenteredLabel>
                <ValueTypography variant="body1Light">{society?.siret}</ValueTypography>
              </ContainerLine>
              {(society?.addressCountry || society?.addressCountryCode) && (
                <ContainerLine>
                  <CenteredLabel variant="body1Light" component="p">
                    <HomeWorkIcon />
                    Pays:
                  </CenteredLabel>
                  <ValueTypography variant="body1Light">
                    {society?.addressCountry || society?.addressCountryCode}
                  </ValueTypography>
                </ContainerLine>
              )}
              <ContainerLine>
                <CenteredLabel variant="body1Light" component="p">
                  <AccountBalanceIcon /># TVA:
                </CenteredLabel>
                <ValueTypography variant="body1Light">{society?.tva}</ValueTypography>
              </ContainerLine>
              {society?.isClient !== true && (
                <>
                  <ContainerLine>
                    <CenteredLabel variant="body1Light" component="p">
                      <HomeWorkIcon />
                      Portage :
                    </CenteredLabel>
                    <ValueTypography variant="body1Light">{society?.portage ? 'Oui' : 'Non'}</ValueTypography>
                  </ContainerLine>
                  <ContainerLine>
                    <CenteredLabel variant="body1Light" component="p">
                      <HomeWorkIcon />
                      {supportIban
                        ? `${t('ibanLabel', { ns: 'mission' })} :`
                        : `${t('ibanFallbackLabel', { ns: 'mission' })} :`}
                    </CenteredLabel>
                    <ValueTypography variant="body1Light">{society?.IBAN}</ValueTypography>
                  </ContainerLine>
                  <ContainerLine>
                    <CenteredLabel variant="body1Light" component="p">
                      <HomeWorkIcon />
                      {supportIban
                        ? `${t('bicLabel', { ns: 'mission' })} :`
                        : `${t('bicFallbackLabel', { ns: 'mission' })} :`}
                    </CenteredLabel>
                    <ValueTypography variant="body1Light">{society?.BIC}</ValueTypography>
                  </ContainerLine>
                  {society?.KBIS && (
                    <ContainerLine>
                      <CenteredLabel variant="body1Light" component="p">
                        <FileOpenIcon />
                        KBIS ({society.KbisExpirationDate || 'Expiration date not found'}):
                      </CenteredLabel>
                      <IconButton
                        onClick={() => setFilePath(society?.KBIS?.toString())}
                        title={society?.KBIS?.toString()}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </ContainerLine>
                  )}
                  {society?.URSSAFVigilanceCertificate && (
                    <ContainerLine>
                      <CenteredLabel variant="body1Light" component="p">
                        <FileOpenIcon />
                        URSSAF ({society.URSSAFVCExpirationDate || 'Expiration date not found'}) :
                      </CenteredLabel>
                      <IconButton
                        onClick={() => setFilePath(society?.URSSAFVigilanceCertificate?.toString())}
                        title={society?.URSSAFVigilanceCertificate?.toString()}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </ContainerLine>
                  )}
                  {society?.RCPVigilanceCertificate && (
                    <ContainerLine>
                      <CenteredLabel variant="body1Light" component="p">
                        <FileOpenIcon />
                        RCP :
                      </CenteredLabel>
                      <IconButton
                        onClick={() => setFilePath(society?.RCPVigilanceCertificate?.toString())}
                        title={society?.RCPVigilanceCertificate?.toString()}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </ContainerLine>
                  )}
                </>
              )}
            </ContainerData>
          </CardContent>
          {!society?.isClient && (
            <Button
              sx={{ position: 'absolute', right: 10, bottom: 10 }}
              textButton="Modifier"
              onClick={() => {
                setOpenPopinEditSociety(true);
              }}
            />
          )}
        </Card>
      ) as React.ReactNode;
    }
    case 'bloomer': {
      const bloomer = data.bloomer as BrowserBloomer;

      return (
        <BloomerCard
          bloomer={bloomer}
          missionOrigin={data.missionOrigin}
          filePath={filePath}
          setFilePath={setFilePath}
        />
      ) as React.ReactNode;
    }
    case 'contact': {
      const { extraInfoTitle, contact } = data;
      return (
        <Card>
          <CardContent>
            <CardHeader
              title={`Le contact ${extraInfoTitle ? extraInfoTitle : ''}`}
              titleTypographyProps={{ variant: 'heading6' }}
            />
            {contact && Array.isArray(contact) && contact.length > 1 ? (
              <Carousel childs={contact.map((c) => getContactComponent(c))}></Carousel>
            ) : (
              getContactComponent(Array.isArray(contact) ? contact[0] : contact)
            )}
          </CardContent>
        </Card>
      ) as React.ReactNode;
    }
    case 'mission': {
      const { mission, actions, currentExtensionAssessment } = data;

      return (
        <Card>
          <CardContent>
            <CardHeader title={'La mission'} titleTypographyProps={{ variant: 'heading6' }} />
            <ContainerData>
              <ContainerLine style={{ alignItems: 'center' }}>
                <CenteredLabel id="typeMissionCard" variant="body1Light" component="p">
                  <LocalOfferIcon />
                  Type:
                </CenteredLabel>
                &nbsp;
                <TagMissionType type={mission?.type} />
              </ContainerLine>
              <ContainerLine style={{ alignItems: 'center' }}>
                <CenteredLabel id="labelMissionCard" variant="body1Light" component="p">
                  <LocalOfferIcon />
                  Label:
                </CenteredLabel>
                <ValueTypography id="labelMissionCardValue" variant="body1Light">
                  {mission?.label}
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="beginMissionMissionCard" variant="body1Light" component="p">
                  <TodayIcon />
                  Début de mission:
                </CenteredLabel>
                <ValueTypography id="beginMissionlMissionCardValue" variant="body1Light">
                  {convertDateToStringFormat(mission?.beginMission, 'dd/MM/yyyy')}
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="endMissionMissionCard" variant="body1Light" component="p">
                  <TodayIcon />
                  Fin de la mission:
                </CenteredLabel>
                <ValueTypography id="endMissionMissionCardValue" variant="body1Light">
                  {convertDateToStringFormat(mission?.endMission, 'dd/MM/yyyy')}
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="purchaseMissionCard" variant="body1Light" component="p">
                  <AttachMoneyIcon />
                  Prix d&apos;achat:
                </CenteredLabel>
                <ValueTypography id="purchaseMissionCardValue" variant="body1Light">
                  {mission?.purchasePricePerDayWithoutTaxes} €
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="saleMissionCard" variant="body1Light" component="p">
                  <AttachMoneyIcon />
                  Prix de vente:
                </CenteredLabel>
                <ValueTypography id="saleMissionCardValue" variant="body1Light">
                  {mission?.salePricePerDayWithoutTaxes} €
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="taxesMissionCard" variant="body1Light" component="p">
                  <EmojiSymbolsIcon />
                  Taxes:
                </CenteredLabel>
                <ValueTypography id="taxesMissionCardValue" variant="body1Light">
                  {mission?.taxes} %
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="taxesMissionCard" variant="body1Light" component="p">
                  <EmojiSymbolsIcon />
                  Type de contrat:
                </CenteredLabel>
                <ValueTypography id="taxesMissionCardValue" variant="body1Light">
                  {mission?.contractType ? mission?.contractType : '⚠️⚠️⚠️ INFORMATION MANQUANTE ⚠️⚠️⚠️'}
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="bloomerPaydateDaysMissionCard" variant="body1Light" component="p">
                  <EmojiSymbolsIcon />
                  Delais de paiement Bloomer:
                </CenteredLabel>
                <ValueTypography id="bloomerPaydateDaysMissionCardValue" variant="body1Light">
                  {mission?.bloomerPaydateDays}
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="clientPaydateDaysMissionCard" variant="body1Light" component="p">
                  <EmojiSymbolsIcon />
                  Delais de paiement Client:
                </CenteredLabel>
                <ValueTypography id="clientPaydateDaysMissionCardValue" variant="body1Light">
                  {mission?.clientPaydateDays}
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="hoursPerDaysMissionCard" variant="body1Light" component="p">
                  <EmojiSymbolsIcon />
                  Base horaire contractuelle:
                </CenteredLabel>
                <ValueTypography id="hoursPerDaysMissionCardValue" variant="body1Light">
                  {mission?.hoursPerDays}
                </ValueTypography>
              </ContainerLine>
              <ContainerLine>
                <CenteredLabel id="hoursPerDaysMissionCard" variant="body1Light" component="p">
                  <EmojiSymbolsIcon />
                  Peut uploader le CRA Client:
                </CenteredLabel>
                <ValueTypography id="enableCRAUploadMissionCardValue" variant="body1Light">
                  {mission?.enableCRAUpload ? 'Oui' : 'Non'}
                </ValueTypography>
              </ContainerLine>
              <CurrentExtensionAssessmentLine extensionAssessment={currentExtensionAssessment} />
              <ContainerActionButton>
                <Menu id="actionsMissionCardButton" title="Actions" menuList={actions} />
              </ContainerActionButton>
            </ContainerData>
          </CardContent>
        </Card>
      ) as React.ReactNode;
    }
    default:
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return (<></>) as React.ReactNode;
  }
};

export default MissionDetailCard;

const Card = styled(RawCard)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  maxWidth: 400,
  margin: 20,
  [theme.breakpoints.up('lg')]: {
    maxWidth: 600,
  },
}));

const CardHeader = styled(RawCardHeader)(({ theme }) => ({
  textTransform: 'uppercase',
  textAlign: 'left',
}));

const ContainerLine = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '8px',
}));

const ValueTypography = styled(Typography)(({ theme }) => ({
  paddingLeft: 5,
  color: theme.palette.blue[60],
  textAlign: 'left',
  whiteSpace: 'normal',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  flex: 1,
  minWidth: 0,
}));

const ContainerData = styled('div')(() => ({
  paddingLeft: 5,
}));

const ContainerActionButton = styled('div')(() => ({
  float: 'right',
  margin: '0 0.5em 0.5em 0',
}));

const CenteredLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textAlign: 'left',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
  },
})) as typeof Typography;
// Typescript limitation https://github.com/mui/material-ui/issues/15695

const DarkTypo = styled(Typography)(({ theme }) => ({
  paddingLeft: 5,
  color: theme.palette.blue[60],
}));

const HrefLine = styled('a')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

const iconCSS = (args: { theme: Theme }) => ({
  paddingRight: 10,
  color: args.theme.palette.neutral[50],
  width: 20,
});

const iconVCSS = (args: { theme: Theme }) => ({
  paddingTop: 0,
  color: args.theme.palette.neutral[50],
  width: 20,
});

const PermIdentityIcon = styled(RawPermIdentityIcon)(iconCSS);
const MailOutlineIcon = styled(RawMailOutlineIcon)(iconCSS);
const PhoneIcon = styled(RawPhoneIcon)(iconCSS);
const WorkOutlineIcon = styled(RawWorkOutlineIcon)(iconCSS);
const HomeWorkIcon = styled(RawHomeWorkIcon)(iconCSS);
const AccountBalanceIcon = styled(RawAccountBalanceIcon)(iconCSS);
const HttpsIcon = styled(RawHttpsIcon)(iconCSS);
const AttachMoneyIcon = styled(RawAttachMoneyIcon)(iconCSS);
const TodayIcon = styled(RawTodayIcon)(iconCSS);
const LocalOfferIcon = styled(RawLocalOfferIcon)(iconCSS);
const EmojiSymbolsIcon = styled(RawEmojiSymbolsIcon)(iconCSS);
const FileOpenIcon = styled(RawFileOpenIcon)(iconCSS);
const VisibilityIcon = styled(RawVisibilityIcon)(iconVCSS);
const IconButton = styled(RawIconButton)(({ theme }) => ({
  paddingTop: '0px',
  paddingBottom: '0px',
}));
